/**
 * One report widget: card chrome + the right chart for its `kind`, plus the
 * non-ok states (not tracked, restricted, error). Every chart can switch to a
 * table view, every widget exports as CSV, and every live widget states its
 * scope (dates, filters, population, sources) on screen and in the CSV.
 */

import { Suspense, lazy, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { BarList, ColumnChart, Donut, Empty, Funnel, Heatmap, KpiRow, LineChart } from "./charts.jsx";
import { DataTable } from "./DataTable.jsx";
import { Card, EmptyState, ErrorState, GhostButton, SectionTitle, Skeleton } from "./primitives.jsx";
import { ButtonLink, Segmented } from "./ui/index.js";
import { asTable } from "../lib/asTable.js";
import { useAuth } from "../auth/AuthProvider.jsx";
import { P } from "../../shared/permissions.js";
import { downloadCsv, toCsv } from "../lib/csv.js";
import { csvMetadata } from "../lib/scope.js";
import { MeasureBadge, ScopePanel, ScopeWarnings } from "./Scope.jsx";

// A map and its geometry load only when someone opens that map view.
const IndiaMap = lazy(() => import("./IndiaMap.jsx"));
const WorldMap = lazy(() => import("./WorldMap.jsx"));

const SPAN = {
  3: "lg:col-span-3",
  4: "lg:col-span-4",
  6: "lg:col-span-6",
  8: "lg:col-span-8",
  12: "lg:col-span-12",
};

function Body({ widget, view, onExportView }) {
  const navigate = useNavigate();
  const { search } = useLocation();
  const { kind, data } = widget;
  if (view === "map") {
    return (
      <Suspense fallback={<Skeleton height={360} />}>
        {data.geo === "country" ? <WorldMap data={data} /> : <IndiaMap data={data} />}
      </Suspense>
    );
  }
  if (view === "table" || kind === "table") {
    const t = asTable(widget);
    // Content tables link each row to that item's details page (filters carry over).
    const link = data?.link;
    const open = link
      ? (row) => {
          const id = row[link.idKey];
          if (!id) return;
          if (link.to === "user") return navigate({ pathname: `/user/${id}`, search });
          const type = link.type ?? row[link.typeKey];
          if (type) navigate({ pathname: `/content/${type}/${id}`, search });
        }
      : undefined;
    return <DataTable columns={t.columns} rows={t.rows} onRowClick={open} tabs={data?.tabs} filters={data?.filters} onExportView={onExportView} />;
  }
  switch (kind) {
    // A ranked-list payload on an x/y kind falls back to bars rather than crashing the page.
    case "line":
      if (!Array.isArray(data?.x)) return data?.items ? <BarList data={data} /> : <Empty />;
      return data.x.length ? <LineChart data={data} /> : <Empty />;
    case "columns":
      if (!Array.isArray(data?.x)) return data?.items ? <BarList data={data} /> : <Empty />;
      return data.x.length ? <ColumnChart data={data} /> : <Empty />;
    case "bars":
      return <BarList data={data} />;
    case "donut":
      return <Donut data={data} />;
    case "funnel":
      return <Funnel data={data} />;
    case "heatmap":
      return <Heatmap data={data} />;
    default:
      return <Empty text={`Unsupported widget kind "${kind}".`} />;
  }
}

function CardLink({ link }) {
  const { search } = useLocation();
  return (
    <ButtonLink to={{ pathname: link.to, search }} size="xs" iconRight="arrowRight" className="mr-1">
      {link.label}
    </ButtonLink>
  );
}

/** Header button that opens the card's scope panel. `onPage`: it sits on the page, not in a card. */
function ScopeButton({ open, onToggle, onPage = false }) {
  return (
    <GhostButton icon="scope" pressed={open} onClick={onToggle} onPage={onPage} title="What this card counts: dates, filters, population and sources">
      Scope
    </GhostButton>
  );
}

/** `page` is the report response the widget came from (tenant, window, filters), for its scope and CSV header. */
export function Widget({ widget: raw, page, filenameBase, basis, onRetry }) {
  const canExport = useAuth().can(P.export);
  // "View scope & measurement" on the page opens every card's Scope panel (URL ?scope=1).
  const allScopes = useSearchParams()[0].get("scope") === "1";
  const [scopeOwn, setScopeOpen] = useState(null);
  const scopeOpen = scopeOwn ?? allScopes;
  // null until someone picks a view; the default depends on the data (see `view` below).
  const [picked, setView] = useState(null);
  // Cards with `data.variants` (e.g. Daily / Monthly) switch the whole card:
  // chart, table, CSV and info all follow the chosen variant.
  const variants = raw.status === "ok" && Array.isArray(raw.data?.variants) ? raw.data.variants : null;
  const [variantKey, setVariantKey] = useState(null);
  const variant = variants?.find((v) => v.key === variantKey) ?? variants?.[0];
  const widget = variant ? { ...raw, kind: variant.kind ?? raw.kind, data: variant.data, info: variant.info ?? raw.info } : raw;
  const span = SPAN[widget.span] ?? SPAN[6];

  const scopeButton = widget.scope && <ScopeButton open={scopeOpen} onToggle={() => setScopeOpen(!scopeOpen)} />;

  if (widget.kind === "kpis" && widget.status === "ok") {
    // KPI tiles sit on the page unless they are a "panel" inside one card.
    const kpiScope = widget.scope && <ScopeButton open={scopeOpen} onToggle={() => setScopeOpen(!scopeOpen)} onPage={widget.layout !== "panel"} />;
    const head = widget.title ? (
      <SectionTitle hint={widget.info} actions={kpiScope} subtitle={widget.subtitle}>
        {widget.title}
        <MeasureBadge widget={widget} />
      </SectionTitle>
    ) : (
      // No title: the Scope button on its own line, right-aligned, with room before the tiles.
      kpiScope && <div className="mb-3 flex justify-end">{kpiScope}</div>
    );
    // "panel": the KPIs inside one card with its title, like the mockup's "Observed activity".
    if (widget.layout === "panel") {
      return (
        <Card className={`col-span-12 ${span}`}>
          {head}
          <ScopeWarnings scope={widget.scope} />
          {scopeOpen && <ScopePanel widget={widget} page={page} />}
          <KpiRow data={widget.data} basis={basis} layout="panel" />
        </Card>
      );
    }
    return (
      <div className={`col-span-12 ${span}`}>
        {head}
        <ScopeWarnings scope={widget.scope} />
        {scopeOpen && <ScopePanel widget={widget} page={page} />}
        <KpiRow data={widget.data} basis={basis} />
      </div>
    );
  }

  const ok = widget.status === "ok";
  const chart = ok && widget.kind !== "table";
  const geo = ok && Boolean(widget.data?.geo);
  // Geo cards open on their map; everything else on its chart.
  const view = picked === "map" && !geo ? "chart" : (picked ?? (geo ? "map" : "chart"));
  const views = geo
    ? [
        ["chart", "Bars"],
        ["map", "Map"],
        ["table", "Table"],
      ]
    : chart
      ? [
          ["chart", "Chart"],
          ["table", "Table"],
        ]
      : [];

  return (
    <Card className={`col-span-12 ${span} flex min-w-0 flex-col`}>
      <SectionTitle
        hint={widget.info}
        subtitle={widget.subtitle}
        actions={
          ok && (
            <>
              {/* Card links (mockup "Open queue →"): where to act on this card; filters carry over. */}
              {widget.links?.map((l) => (
                <CardLink key={l.to} link={l} />
              ))}
              {variants && variants.length > 1 && (
                <Segmented
                  label={raw.data.variantLabel ?? "Period"}
                  size="xs"
                  className="mr-1"
                  value={variant.key}
                  onChange={setVariantKey}
                  options={variants.map((v) => ({ value: v.key, label: v.label, title: `Show ${v.label.toLowerCase()}` }))}
                />
              )}
              {views.length > 0 && (
                <Segmented
                  label="View"
                  size="xs"
                  className="mr-1"
                  value={view}
                  onChange={setView}
                  options={views.map(([id, label]) => ({ value: id, label, title: `Show as ${label.toLowerCase()}` }))}
                />
              )}
              {scopeButton}
              {canExport && (
                <GhostButton
                  icon="download"
                  title="Download as CSV (starts with the card's scope)"
                  onClick={() => {
                    const t = asTable(widget);
                    // Buttons are not data.
                    t.columns = t.columns.filter((c) => c.format !== "action");
                    const meta = csvMetadata({ ...widget, variantLabel: variant?.label }, page);
                    downloadCsv(`${filenameBase}_${widget.id}${variant ? `_${variant.key}` : ""}.csv`, toCsv(t.columns, t.rows, { meta }));
                  }}
                >
                  CSV
                </GhostButton>
              )}
            </>
          )
        }
      >
        {widget.title || "Key metrics"}
        <MeasureBadge widget={widget} />
      </SectionTitle>
      <div className="min-w-0 flex-1">
        {ok && <ScopeWarnings scope={widget.scope} />}
        {ok && scopeOpen && <ScopePanel widget={widget} page={page} />}
        {/* Tables with action buttons say it themselves. */}
        {ok && widget.data?.link && view !== "map" && !widget.data.columns?.some((c) => c.format === "action") && (
          <p className="-mt-1 mb-2 text-[11px] text-ink-3">{widget.data.link.to === "user" ? "Click a row to open that user." : "Click a row for its detailed analytics."}</p>
        )}
        {ok && (
          <Body
            widget={widget}
            view={view}
            onExportView={
              canExport
                ? (rows, { tab, query, filters }) => {
                    const t = asTable(widget);
                    const columns = t.columns.filter((c) => c.format !== "action");
                    const view = [tab && `tab "${tab}"`, filters, query && `search "${query}"`].filter(Boolean).join(", ");
                    const meta = [...csvMetadata({ ...widget, variantLabel: variant?.label }, page), ["This view", `${view} · ${rows.length} of ${t.rows.length} rows`]];
                    downloadCsv(`${filenameBase}_${widget.id}_view.csv`, toCsv(columns, rows, { meta }));
                  }
                : undefined
            }
          />
        )}
        {/* No source yet: "Not measured", never an empty chart or a zero. */}
        {widget.status === "not-tracked" && <EmptyState title="Not measured" detail={widget.reason} />}
        {widget.status === "restricted" && (
          <EmptyState title="Access restricted" detail="Your role does not include this card's data for this tenant. Nothing from it was sent to your browser." />
        )}
        {widget.status === "error" && (
          <ErrorState what={widget.title?.toLowerCase() || "this widget"} message={widget.error?.message} retryable={widget.error?.retryable !== false} onRetry={onRetry} />
        )}
      </div>
    </Card>
  );
}

export function WidgetSkeleton({ span = 6 }) {
  return (
    <Card className={`col-span-12 ${SPAN[span] ?? SPAN[6]}`}>
      <Skeleton height={10} width={120} className="mb-4" />
      <Skeleton height={180} />
    </Card>
  );
}
